import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  SimpleGrid,
  Badge,
  Divider,
} from "@chakra-ui/react";
import Header from "./Header";
import Footer from "./Footer";
import { MAIN, MAIN_BACKGROUND, SECONDARY, SECONDARY_BACKGROUND } from "./colours";
import { getEvent } from "./events/eventConfig";
import { fetchAvailability } from "./booking/api";
import BookingForm from "./booking/BookingForm";

function Band({ bg, color, children, id }) {
  return (
    <Flex id={id} bgColor={bg} justify="center" py={{ base: "16", md: "24" }}>
      <Box px={{ base: "6", md: "10", lg: "24" }} maxW="1100px" w="100%" color={color}>
        {children}
      </Box>
    </Flex>
  );
}

export default function EventPage() {
  const { slug } = useParams();
  const event = getEvent(slug);

  const [availability, setAvailability] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(true);

  useEffect(() => {
    let active = true;
    if (!event) return;
    setAvailabilityLoading(true);
    fetchAvailability(event.id)
      .then((data) => {
        if (active) setAvailability(data.sittings);
      })
      .catch(() => {
        // If availability can't load we let the form still render; the server
        // re-checks at payment time so we never oversell.
        if (active) setAvailability(null);
      })
      .finally(() => {
        if (active) setAvailabilityLoading(false);
      });
    return () => {
      active = false;
    };
  }, [event]);

  if (!event) {
    return (
      <>
        <Header />
        <Band bg={MAIN_BACKGROUND} color={MAIN}>
          <Heading size="xl">Event not found</Heading>
          <Text mt="4">Sorry, we couldn't find that event.</Text>
          <Button
            as="a"
            href="/"
            mt="8"
            bg={MAIN}
            color={MAIN_BACKGROUND}
            borderRadius="none"
            _hover={{ bg: "transparent", color: MAIN, border: `2px solid ${MAIN}` }}
          >
            Back home
          </Button>
        </Band>
        <Footer />
      </>
    );
  }

  const scrollToBook = () =>
    document.getElementById("book").scrollIntoView({ behavior: "smooth" });

  return (
    <>
      <Header />

      {/* Hero */}
      <Band bg={MAIN_BACKGROUND} color={MAIN}>
        <Text fontSize="md" letterSpacing="2px" mb="3" textTransform="uppercase">
          Happy Fig Supper Club
        </Text>
        <Heading size={{ base: "2xl", lg: "3xl" }} lineHeight="1.1" mb="4">
          {event.title}
        </Heading>
        <Text fontSize="xl" mb="1">
          {event.tagline}
        </Text>
        <Text fontSize="xl" fontWeight="bold" mb="6">
          {event.dateLabel}
        </Text>

        <SimpleGrid columns={{ base: 1, sm: 2 }} spacing="4" maxW="640px" mb="8">
          {event.sittings.map((s) => {
            const info = availability ? availability[s.id] : null;
            const soldOut = info ? info.soldOut : false;
            return (
              <Box key={s.id} border={`2px solid ${MAIN}`} p="4">
                <Flex justify="space-between" align="center" mb="1">
                  <Text fontWeight="bold">{s.label}</Text>
                  {soldOut && (
                    <Badge bg="#a94442" color="white" borderRadius="none">
                      Sold out
                    </Badge>
                  )}
                </Flex>
                <Text fontSize="lg">{s.time}</Text>
              </Box>
            );
          })}
        </SimpleGrid>

        <Flex gap="4" align="center" wrap="wrap">
          <Button
            size="lg"
            bg={MAIN}
            color={MAIN_BACKGROUND}
            border={`2px solid ${MAIN}`}
            borderRadius="none"
            _hover={{ bg: "transparent", color: MAIN }}
            onClick={scrollToBook}
          >
            Book now
          </Button>
          <Text fontSize="xl" fontWeight="bold">
            {event.priceLabel}
          </Text>
        </Flex>
      </Band>

      {/* Description */}
      <Band bg={SECONDARY_BACKGROUND} color={SECONDARY}>
        <Heading size="xl" mb="6">
          The evening
        </Heading>
        {event.description.map((p, i) => (
          <Text key={i} fontSize="lg" mb="5" maxW="760px">
            {p}
          </Text>
        ))}
      </Band>

      {/* Sample menu */}
      <Band bg={MAIN_BACKGROUND} color={MAIN}>
        <Heading size="xl" mb="3">
          Menu
        </Heading>
        <Text fontSize="md" mb="8" opacity={0.85} maxW="760px">
          {event.menu.note}
        </Text>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing="10">
          {event.menu.courses.map((course) => (
            <Box key={course.name}>
              <Heading size="md" mb="4">
                {course.name}
              </Heading>
              <Divider borderColor={MAIN} opacity={0.4} mb="4" />
              {course.items.map((item) => (
                <Text key={item} fontSize="lg" mb="3">
                  {item}
                </Text>
              ))}
            </Box>
          ))}
        </SimpleGrid>
      </Band>

      {/* Venue & parking */}
      <Band bg={SECONDARY_BACKGROUND} color={SECONDARY}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12">
          <Box>
            <Heading size="lg" mb="4">
              Venue
            </Heading>
            <Text fontWeight="bold" fontSize="lg" mb="3">
              {event.venue.name}
            </Text>
            {event.venue.details.map((d, i) => (
              <Text key={i} fontSize="lg" mb="3">
                {d}
              </Text>
            ))}
          </Box>
          <Box>
            <Heading size="lg" mb="4">
              Parking & arrival
            </Heading>
            {event.parking.map((p, i) => (
              <Text key={i} fontSize="lg" mb="3">
                {p}
              </Text>
            ))}
          </Box>
        </SimpleGrid>
      </Band>

      {/* Booking form */}
      <Band bg={MAIN_BACKGROUND} color={MAIN} id="book">
        <BookingForm
          event={event}
          availability={availability}
          availabilityLoading={availabilityLoading}
        />
      </Band>

      <Footer />
    </>
  );
}
